import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { IMasterDocumentAdapter, DocumentState } from '../adapter/MasterDocumentAdapter.types';
import type { ApiService } from '../adapter/ApiService.types';
import type { MasterDocumentTemplate } from '../template/MasterDocumentTemplate.types';
import { createDefaultAdapter } from '../adapter/createDefaultAdapter';
import { DEFAULT_ICON_REGISTRY, type IconComponent } from './iconRegistry';
import { LookupResolveCacheProvider } from '../LookupReadonlyValue';
import { MasterDocumentPrintProvider } from '../printing/MasterDocumentPrintProvider';
import type { MasterDocumentPrintConfig } from '../printing/MasterDocumentPrinting.types';
import { MasterDocumentAuditProvider } from '../audit/MasterDocumentAuditProvider';
import type { AuditHistoryProps } from '../../../audit/types/audit.types';

// ════════════════════════════════════════════════════════════
// FACTBOX WIDGETS — registro de widgets custom
// ════════════════════════════════════════════════════════════

/**
 * Contexto en vivo que recibe un widget FactBox custom (`type: 'custom'`)
 * cuando se registra como función. Permite acceder al estado del documento y
 * al adapter para cablear widgets que dependen de datos en runtime
 * (ej: AdvanceApplicationsPanel necesita documentId, docState, headerValues).
 */
export interface FactBoxWidgetRenderContext {
  documentId: string;
  docState: string;
  mode: DocumentState['mode'];
  headerValues: Record<string, unknown>;
  /** Datos resueltos de la sección (según `dataSource`). */
  data: Record<string, unknown>;
  adapter: IMasterDocumentAdapter;
  /**
   * Resincroniza el documento contra el servidor (encabezado, líneas, totales,
   * `row_version`). Invócalo DESPUÉS de persistir por tu cuenta: el kit solo
   * mergea lo que guarda él, así que un widget que escribe contra su propio
   * endpoint —cruzar una OC, aplicar un anticipo— deja la grilla de Líneas y el
   * widget de Totales mostrando los valores previos hasta que alguien recarga la
   * página.
   *
   * Es el mismo `reload` que devuelve {@link useMasterDocumentReload}: usa el
   * hook si tu widget es un componente y prefieres no cablearlo por props.
   */
  reload: () => Promise<void>;
  /**
   * Sube tras cada mutación del kit. Úsalo como dependencia del fetch propio del
   * widget para que su vista se reconsulte cuando el documento cambió por fuera
   * de él —el caso típico es una grilla de cantidades pendientes que quedaría
   * stale tras un cruce.
   */
  refreshToken: number;
}

/**
 * Valor registrable en `factBoxWidgets`. Puede ser un nodo estático o una
 * función que recibe el contexto en vivo del documento y retorna el nodo.
 */
export type FactBoxWidget = ReactNode | ((ctx: FactBoxWidgetRenderContext) => ReactNode);

// ════════════════════════════════════════════════════════════
// LINE TAB RENDERERS — pestañas de detalle Tipo 2 (FlexQuery) y Tipo 3 (Render)
// ════════════════════════════════════════════════════════════

/**
 * Renderer de una pestaña de detalle, referido por clave desde el template
 * (`layout.detail.tabs[].renderKey`).
 *
 * Mismo mecanismo que `factBoxWidgets` y por la misma razón: el componente es del
 * host —una consulta FlexQuery, un panel propio— y el template solo transporta su
 * clave. Así el kit no importa nada del host ni el host tiene que reconstruir el
 * estado del documento: lo recibe resuelto en el contexto.
 *
 * @see LineTabRenderContext - Lo que recibe el renderer
 * @see DetailTabTemplate - La declaración en el template
 */
export type LineTabRenderer = (
  ctx: import('../MasterDocument.types').LineTabRenderContext,
) => ReactNode;

// ════════════════════════════════════════════════════════════
// CONTEXTS
// ════════════════════════════════════════════════════════════

const AdapterContext = createContext<IMasterDocumentAdapter | null>(null);
const TemplateContext = createContext<MasterDocumentTemplate | null>(null);
const IconContext = createContext<Record<string, IconComponent>>(DEFAULT_ICON_REGISTRY);
const FactBoxWidgetContext = createContext<Record<string, FactBoxWidget>>({});
const LineTabRendererContext = createContext<Record<string, LineTabRenderer>>({});
// null = sistema de permisos no activo → mostrar y habilitar todo (fallback permisivo)
const PermissionsContext = createContext<string[] | null>(null);

// ════════════════════════════════════════════════════════════
// HOOKS
// ════════════════════════════════════════════════════════════

export function useMasterDocumentAdapter(): IMasterDocumentAdapter {
  const adapter = useContext(AdapterContext);
  if (!adapter) {
    throw new Error(
      'useMasterDocumentAdapter debe usarse dentro de un <MasterDocumentProvider>',
    );
  }
  return adapter;
}

export function useMasterDocumentTemplate(): MasterDocumentTemplate {
  const template = useContext(TemplateContext);
  if (!template) {
    throw new Error(
      'useMasterDocumentTemplate debe usarse dentro de un <MasterDocumentProvider>',
    );
  }
  return template;
}

export function useIconRegistry(): Record<string, IconComponent> {
  return useContext(IconContext);
}

export function useFactBoxWidgets(): Record<string, FactBoxWidget> {
  return useContext(FactBoxWidgetContext);
}

/**
 * Renderers de pestañas de detalle registrados por el host. Vacío = ninguno, y
 * entonces una pestaña Tipo 2 o Tipo 3 del template queda sin contenido: es un
 * error de cableado del consumidor, no del kit, y se avisa por consola.
 */
export function useLineTabRenderers(): Record<string, LineTabRenderer> {
  return useContext(LineTabRendererContext);
}

/**
 * Retorna los permisos del usuario activo, o `null` si el sistema de permisos
 * no está configurado en el provider (fallback permisivo: todo visible y editable).
 */
export function useUserPermissions(): string[] | null {
  return useContext(PermissionsContext);
}

// ════════════════════════════════════════════════════════════
// PROVIDER PROPS — Dos modos de uso
// ════════════════════════════════════════════════════════════

interface BaseProviderProps {
  template: MasterDocumentTemplate;
  icons?: Record<string, IconComponent>;
  factBoxWidgets?: Record<string, FactBoxWidget>;
  /**
   * Renderers de las pestañas de detalle Tipo 2 (FlexQuery) y Tipo 3 (Render),
   * indexados por la `renderKey` que declara `template.layout.detail.tabs`.
   *
   * Ausente ⇒ el documento solo puede tener pestañas de tipo `lines`, que es el
   * comportamiento de siempre. El kit no trae ninguna consulta FlexQuery propia:
   * ese componente vive en la app host y entra por aquí.
   *
   * @example
   * ```tsx
   * <MasterDocumentProvider
   *   template={template}
   *   adapter={adapter}
   *   lineTabRenderers={{
   *     flexQuery: (ctx) => (
   *       <FlexQueryGrid
   *         queryCode={ctx.flexQuery!.queryCode}
   *         filters={ctx.filters}
   *         refreshToken={ctx.refreshToken}
   *         rowActions={ctx.rowActions}
   *       />
   *     ),
   *   }}
   * >
   * ```
   */
  lineTabRenderers?: Record<string, LineTabRenderer>;
  /**
   * Permisos del usuario activo. Cada string es una clave de permiso
   * (e.g. "view.cedula", "edit.precio").
   *
   * - Ausente / null → sistema de permisos no activo: todos los campos visibles y editables.
   * - [] → sistema activo pero sin permisos: campos restringidos quedan ocultos/deshabilitados.
   */
  userPermissions?: string[] | null;
  /**
   * Configuración de impresión nativa (opcional). Cuando se provee, el kit
   * muestra "Imprimir" en el menú de cada fila del listado y como botón en la
   * cabecera del detalle, abriendo un modal de vista previa con el visor que el
   * host inyecta. Sin esta prop, el comportamiento es idéntico al actual
   * (sin impresión) → retrocompatibilidad total.
   *
   * @see MasterDocumentPrintConfig
   */
  printing?: MasterDocumentPrintConfig;
  /**
   * Configuración de historial de auditoría (opcional). Mismo contrato que MasterCrud.
   * Cuando se provee y `enabled` es true, el detalle del documento muestra una acción
   * ⏱ que abre el historial de cambios (campo por campo) del documento guardado. Sin
   * esta prop, el comportamiento es idéntico al actual (sin auditoría) →
   * retrocompatibilidad total. La evaluación del permiso es responsabilidad del
   * consumidor, comunicada vía `enabled`.
   *
   * @see AuditHistoryProps
   */
  auditHistory?: AuditHistoryProps;
  children: ReactNode;
}

/**
 * Modo manual: el consumidor provee su propio adapter.
 */
interface ManualAdapterProps extends BaseProviderProps {
  endpointAuto?: false;
  adapter: IMasterDocumentAdapter;
  apiService?: never;
}

/**
 * Modo auto: usa los endpoints estándar del contrato.
 * El consumidor solo entrega un apiService con baseUrl y headers.
 */
interface AutoEndpointProps extends BaseProviderProps {
  endpointAuto: true;
  apiService: ApiService;
  adapter?: never;
}

export type MasterDocumentProviderProps = ManualAdapterProps | AutoEndpointProps;

// ════════════════════════════════════════════════════════════
// PROVIDER
// ════════════════════════════════════════════════════════════

export function MasterDocumentProvider(props: MasterDocumentProviderProps) {
  const {
    template,
    icons,
    factBoxWidgets,
    lineTabRenderers,
    userPermissions = null,
    printing,
    auditHistory,
    children,
  } = props;

  // Resolver el adapter según el modo
  const adapter = useMemo<IMasterDocumentAdapter>(() => {
    if (props.endpointAuto) {
      return createDefaultAdapter(props.apiService, template);
    }
    return props.adapter;
  }, [
    props.endpointAuto,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    props.endpointAuto ? props.apiService : props.adapter,
    template,
  ]);

  const mergedIcons = useMemo(
    () => (icons ? { ...DEFAULT_ICON_REGISTRY, ...icons } : DEFAULT_ICON_REGISTRY),
    [icons],
  );

  const widgets = useMemo(
    () => factBoxWidgets ?? {},
    [factBoxWidgets],
  );

  const tabRenderers = useMemo(
    () => lineTabRenderers ?? {},
    [lineTabRenderers],
  );

  return (
    <AdapterContext.Provider value={adapter}>
      <TemplateContext.Provider value={template}>
        <IconContext.Provider value={mergedIcons}>
          <FactBoxWidgetContext.Provider value={widgets}>
            <LineTabRendererContext.Provider value={tabRenderers}>
              <PermissionsContext.Provider value={userPermissions}>
                <LookupResolveCacheProvider>
                  <MasterDocumentPrintProvider
                    printing={printing}
                    documentLabel={template.identity.label}
                  >
                    <MasterDocumentAuditProvider auditHistory={auditHistory}>
                      {children}
                    </MasterDocumentAuditProvider>
                  </MasterDocumentPrintProvider>
                </LookupResolveCacheProvider>
              </PermissionsContext.Provider>
            </LineTabRendererContext.Provider>
          </FactBoxWidgetContext.Provider>
        </IconContext.Provider>
      </TemplateContext.Provider>
    </AdapterContext.Provider>
  );
}
